<template>
  <v-dialog v-model="dialog" persistent :width="$vuetify.display.smAndDown ? 'auto' : 950">
    <AlFormCard title="Site Detail" :subtitle="subtitle">
      <template #error>
        <ErrorAlert
          v-if="error != null"
          :error="error"
          @clearErrors="clearErrors()"
          class="mb-0 mb-4 rounded"
        />
      </template>

      <template #appendAction>
        <v-btn
          variant="tonal"
          class="rounded"
          size="32"
          @click="closeDialog()"
          icon
          color="default"
          density="comfortable"
        >
          <v-icon size="18" icon="mdi-close" />
        </v-btn>
      </template>

      <v-form ref="form" v-model="valid" validate-on="submit lazy">
        <h2 class="text-subtitle-2 font-weight-medium my-3">Site Info</h2>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="siteData.name"
              label="Site Name"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :error-messages="error && (error.fieldErrors['name'] as string[])"
            ></v-text-field>
          </v-col>
        </v-row>

        <h2 class="text-subtitle-2 font-weight-medium my-3">Address</h2>

        <v-row class="mb-3">
          <v-col cols="12">
            <v-text-field
              v-model="siteData.address!.address1"
              label="Address1*"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :rules="[requiredValidator]"
              :error-messages="
                error &&
                error.fieldErrors['address'] &&
                ((error.fieldErrors['address'] as IFieldErrors)['address1'] as string[])
              "
            ></v-text-field>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="siteData.address!.address2"
              label="Address2"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :error-messages="
                error &&
                error.fieldErrors['address'] &&
                ((error.fieldErrors['address'] as IFieldErrors)['address2'] as string[])
              "
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="siteData.address!.city"
              label="City*"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :rules="[requiredValidator]"
              :error-messages="
                error &&
                error.fieldErrors['address'] &&
                ((error.fieldErrors['address'] as IFieldErrors)['city'] as string[])
              "
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <RegionComboBox
              v-model="siteData.address!.region"
              :label="`${regionComboBoxLabel}*`"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :rules="[requiredValidator]"
              :error-messages="
                error &&
                error.fieldErrors['address'] &&
                ((error.fieldErrors['address'] as IFieldErrors)['region'] as string[])
              "
              :country="addressCountryCode"
            ></RegionComboBox>
          </v-col>

          <v-col cols="12" md="6">
            <CountryPicker
              v-model="siteData.address!.country"
              label="Country*"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :rules="[requiredValidator]"
              :error-messages="
                error &&
                error.fieldErrors['address'] &&
                ((error.fieldErrors['address'] as IFieldErrors)['country'] as string[])
              "
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="siteData.address!.postal_code"
              label="Postal Code"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :error-messages="
                error &&
                error.fieldErrors['address'] &&
                ((error.fieldErrors['address'] as IFieldErrors)['postal_code'] as string[])
              "
            ></v-text-field>
          </v-col>
        </v-row>
      </v-form>

      <v-alert
        v-if="createdSiteMessage"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-3"
        closable
      >
        {{ createdSiteMessage }}
      </v-alert>

      <SiteGeofenceForm
        v-model="siteData"
        :client-id="clientId"
        :site-code="siteData.code ?? site?.code"
        :on-before-geocode="saveSiteBeforeGeocode"
      />

      <template #actions>
        <v-spacer></v-spacer>
        <v-btn
          v-if="justCreatedSiteCode != null"
          variant="outlined"
          class="me-2"
          @click="closeDialog()"
        >
          Done
        </v-btn>
        <v-btn color="primary" variant="flat" :loading="loading" @click="save()">Save site</v-btn>
      </template>
    </AlFormCard>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { cloneDeep } from 'lodash'
import { useQueryClient } from '@tanstack/vue-query'

import type { ISystemError, IFieldErrors } from '@/models/error'
import type { VForm } from 'vuetify/components'
import { Site, type ISite, type ISiteData, type ISiteFormData } from '@/models/client'
import { requiredValidator } from '@/utils/validators'
import { diffDeep } from '@/utils/helpers'
import { useCreateSite, useUpdateSite, pollSiteUntilGeocodeDone } from '@/composables/site'
import { locationToLatLng } from '@/models/client'

import AlFormCard from '../common/AlFormCard.vue'
import ErrorAlert from '../common/ErrorAlert.vue'
import CountryPicker from '../common/CountryPicker.vue'
import RegionComboBox from '../common/RegionComboBox.vue'
import SiteGeofenceForm from './SiteGeofenceForm.vue'

interface Props {
  site?: ISite
  isEdit: boolean
  clientId: number
}

const props = defineProps<Props>()
const emit = defineEmits<{ 'created-site': [data: ISiteData] }>()

const queryClient = useQueryClient()
const siteData = ref<ISiteFormData>({
  address: {},
  geofences: [{ shape: null }]
})

const dialog = defineModel<boolean>('dialog', { required: true, default: false })

/** After create we stay open and poll; set to new site code so we show message and Done button. */
const justCreatedSiteCode = ref<number | null>(null)
const isPollingAfterCreate = ref(false)

function applyApiSiteToForm(apiSite: ISiteData) {
  const fromLocation = locationToLatLng(apiSite.location)
  siteData.value = {
    ...cloneDeep(apiSite),
    coordinates: fromLocation ?? undefined,
    geofences: [{ shape: null }]
  }
}

function applyPropsSiteToForm() {
  if (!props.site) return
  const cloned = cloneDeep(props.site) as unknown as Record<string, unknown>
  const plain = { ...cloned } as ISiteFormData & Record<string, unknown>
  if (props.site.coordinates) {
    plain.coordinates = {
      latitude: props.site.coordinates.latitude,
      longitude: props.site.coordinates.longitude
    }
  }
  if (!plain.geofences?.length) plain.geofences = [{ shape: null }]
  siteData.value = plain as ISiteFormData
}

const createdSiteMessage = computed(() => {
  if (justCreatedSiteCode.value == null) return null
  if (isPollingAfterCreate.value || siteData.value.geocode_status === 'pending') {
    return 'Site created. Geocoding in progress…'
  }
  if (siteData.value.geocode_status === 'ok') {
    return 'Site created. Location is ready. You can set a geofence below or click Done to close.'
  }
  if (siteData.value.geocode_status === 'failed') {
    return 'Site created. Geocoding failed. You can set location manually on the map or click Done to close.'
  }
  return 'Site created. You can set a geofence below or click Done to close.'
})

watch(dialog, (value) => {
  if (value) {
    clearErrors()
    justCreatedSiteCode.value = null
    if (!props.isEdit) {
      siteData.value = {
        address: { country: 'CA', region: 'ON' },
        geofences: [{ shape: null }]
      }
    } else {
      applyPropsSiteToForm()
    }
  } else {
    if (props.site) applyPropsSiteToForm()
  }
})

const form = ref<VForm>()
const valid = ref(false)

const subtitle = computed(() => `${props.isEdit ? 'Update' : 'Create'} site information`)

const regionComboBoxLabel = computed<string>(() => {
  switch (siteData.value.address!.country) {
    case 'CA':
      return 'Province'
    case 'US':
      return 'State'
    default:
      return 'Region'
  }
})
const addressCountryCode = computed(() => {
  const country = siteData.value.address?.country
  if (country == null) return ''
  return typeof country === 'string' ? country : (country as { code?: string }).code ?? ''
})

const clientId = computed(() => props.clientId!)
const {
  isPending: isUpdatePending,
  error: updateError,
  mutate: updateSite,
  mutateAsync: updateSiteAsync,
  reset: updateReset
} = useUpdateSite(clientId)
const {
  isPending: isCreatePending,
  error: createError,
  mutate: createSite,
  reset: createReset
} = useCreateSite(clientId)

const localError = ref<ISystemError | null>(null)

const loading = computed(
  () =>
    (props.isEdit ? isUpdatePending.value : isCreatePending.value) || isPollingAfterCreate.value
)

const error = computed({
  get: () => {
    return localError.value || props.isEdit ? updateError.value : createError.value
  },
  set: (value) => {
    localError.value = value
  }
})

function clearErrors() {
  props.isEdit ? updateReset() : createReset()
  error.value = null
}

function closeDialog() {
  clearErrors()
  dialog.value = false
}

/** Persist current site (name + address) so backend geocodes the latest address. Call before triggering geocode in edit mode. */
async function saveSiteBeforeGeocode(): Promise<void> {
  if (!props.isEdit || !form.value) return
  clearErrors()
  const { valid } = await form.value.validate()
  if (!valid) throw new Error('Address is invalid')
  let siteInstance: ISite
  try {
    siteInstance = new Site(siteData.value)
  } catch (e: any) {
    error.value = e
    throw e
  }
  const payload = diffDeep(siteInstance, props.site, true) as Partial<ISiteData> & { code: number }
  payload.code = siteInstance.code!
  await updateSiteAsync(payload)
}

async function save() {
  clearErrors()
  const { valid } = await form.value!.validate()

  if (valid) {
    let siteInstance: ISite | null = null

    try {
      siteInstance = new Site(siteData.value)
    } catch (e: any) {
      // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
      error.value = e
    }

    if (siteInstance != null) {
      let payload = siteInstance

      if (props.isEdit) {
        payload = diffDeep(siteInstance, props.site, true)

        payload.code = siteInstance.code!

        // updateSite and createSite Uses different payload structures
        updateSite(payload, {
          onSuccess: () => {
            closeDialog()
          }
        })
      } else {
        createSite(
          { site: payload! },
          {
            onSuccess: async (data: ISiteData) => {
              applyApiSiteToForm(data)
              emit('created-site', data)
              const createdCode = data.code ?? null
              justCreatedSiteCode.value = createdCode
              if (data.geocode_status === 'pending' && data.code != null) {
                isPollingAfterCreate.value = true
                try {
                  const site = await pollSiteUntilGeocodeDone(props.clientId, data.code)
                  // Only apply poll result to form if dialog is still open and still showing this site (ignore stale result)
                  if (dialog.value && justCreatedSiteCode.value === createdCode) {
                    applyApiSiteToForm(site)
                  }
                  // Always invalidate so the list refetches and shows the updated site (e.g. geocode_status: 'ok'), even if dialog was closed
                  queryClient.invalidateQueries({ queryKey: ['sites', props.clientId] })
                } finally {
                  isPollingAfterCreate.value = false
                }
              } else {
                queryClient.invalidateQueries({ queryKey: ['sites', props.clientId] })
              }
            }
          }
        )
      }
    }
  }
}
</script>
